import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Search, ClipboardCheck, MessageSquare, FileText, GraduationCap, ShieldCheck, Users, Target, Clock, BadgeCheck, Handshake, Droplets, Shield, Home, Mail } from 'lucide-react';
import QuoteRequestModal from './QuoteRequestModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesSection = () => {
  const [quoteService, setQuoteService] = useState(null);
  const services = [
    {
      icon: <Search size={40} />,
      title: "Recherche de bateau sur mesure",
      description: "Le client me donne ses critères (budget, type, usage, zone). J'identifie 5-10 bateaux correspondants, présente une short-list avec analyse comparative et organise les visites.",
      features: [
        "Analyse de vos besoins et critères",
        "Sélection de 5-10 bateaux pertinents",
        "Rapport comparatif détaillé",
        "Organisation des visites"
      ],
      tarif: "800 - 1 500 €",
      image: "https://images.pexels.com/photos/945460/pexels-photo-945460.jpeg?auto=compress&cs=tinysrgb&w=800",
      hasTarget: true,
      forBuyer: true
    },
    {
      icon: <ClipboardCheck size={40} />,
      title: "Audit pré-achat (expertise bateau)",
      description: "Vous avez repéré un bateau ? Je l'analyse en détail, liste les points à vérifier, vous accompagne à la visite et rédige un rapport d'expertise complet avec mon feu vert ou non.",
      features: [
        "Analyse détaillée de l'annonce",
        "Checklist de 80+ points de vérification",
        "Visite sur place ou à distance",
        "Rapport d'expertise complet",
        "Recommandation d'achat"
      ],
      tarif: "300 - 700 €",
      image: "https://images.unsplash.com/photo-1737418092729-c73207ce020c?w=800",
      forBuyer: true
    },
    {
      icon: <BadgeCheck size={40} />,
      title: "Pré-validation vendeur",
      description: "Vous vendez votre bateau ? Obtenez une pré-validation pour rassurer vos acheteurs et accélérer la vente. Un gage de confiance qui fait la différence.",
      features: [
        "Audit documentaire complet",
        "Vérification de l'historique",
        "Analyse technique et administrative",
        "Certificat de pré-validation",
        "Badge de confiance pour vos annonces"
      ],
      tarif: "250 - 500 €",
      image: "https://images.unsplash.com/photo-1568818161606-f2ee14826cf3?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
      forSeller: true
    },
    {
      icon: <MessageSquare size={40} />,
      title: "Négociation pour votre compte",
      description: "Je contacte le vendeur en votre nom et utilise mes techniques de vendeur pour obtenir le meilleur prix. Gestion des allers-retours jusqu'à l'accord final.",
      features: [
        "Contact direct avec le vendeur",
        "Techniques de négociation professionnelles",
        "Identification des points faibles",
        "Gestion complète jusqu'à l'accord"
      ],
      tarif: "20-30% de l'économie réalisée",
      image: "https://images.unsplash.com/photo-1561377916-e87f02d85b43?w=800",
      forBuyer: true
    },
    {
      icon: <Handshake size={40} />,
      title: "Mise en relation qualifiée",
      description: "Acheteurs et vendeurs qualifiés mis en contact. Je vérifie le sérieux des deux parties pour des transactions sécurisées et efficaces.",
      features: [
        "Qualification des acheteurs potentiels",
        "Vérification de la solvabilité",
        "Présentation des dossiers complets",
        "Accompagnement jusqu'à la transaction"
      ],
      tarif: "Sur devis",
      image: "https://images.unsplash.com/photo-1531545514256-b1400bc00f31?w=800",
      forBoth: true
    },
    {
      icon: <FileText size={40} />,
      title: "Accompagnement administratif complet",
      description: "Vérification des papiers du bateau, aide à la rédaction du compromis de vente, conseils assurance et place de port. Suivi jusqu'à la signature définitive.",
      features: [
        "Vérification documents (carte grise, francisation)",
        "Rédaction compromis de vente",
        "Conseils assurance et amarrage",
        "Suivi jusqu'à signature"
      ],
      tarif: "400 - 600 €",
      image: "https://images.unsplash.com/photo-1561481006-6b90903b7ef3?w=800",
      forBoth: true
    },
    {
      icon: <GraduationCap size={40} />,
      title: "Formation 'Premier bateau'",
      description: "Coaching personnalisé pour primo-accédant. 3-5 séances visio d'1h30 pour tout comprendre : choisir son bateau, l'entretenir, les pièges à éviter, budget réel.",
      features: [
        "3-5 séances visio de 1h30",
        "Choisir le bon bateau pour débuter",
        "Entretien et budget réel",
        "Support PDF + checklist"
      ],
      tarif: "300 - 500 €",
      image: "https://images.unsplash.com/photo-1561918936-b9524c66618f?w=800",
      forBuyer: true
    },
    {
      icon: <Droplets size={40} />,
      title: "Carénage bateau",
      description: "Sortie d'eau, nettoyage haute pression, traitement antifouling, réparation d'osmose, polissage gel coat. Une coque saine, c'est des années de tranquillité et de meilleures performances.",
      features: [
        "Nettoyage haute pression de la coque",
        "Application antifouling adapté à votre zone",
        "Traitement et réparation d'osmose",
        "Polissage et lustrage gel coat",
        "Inspection passe-coques et anodes"
      ],
      tarif: "Sur devis",
      quoteKey: "carenage",
      image: `${API_URL}/api/uploads/service-carenage-bateau.jpg`,
      forBoth: true
    },
    {
      icon: <Shield size={40} />,
      title: "Gardiennage à l'année",
      description: "Vous laissez votre bateau plusieurs mois ? Je veille sur lui. Rondes régulières, rapports mensuels photo, gestion des urgences (météo, panne, sinistre) et intervention rapide.",
      features: [
        "Rondes régulières au port ou au sec",
        "Rapport mensuel avec photos",
        "Surveillance amarrage, batteries, fonds",
        "Gestion d'urgence 7j/7",
        "Liaison directe avec votre assurance"
      ],
      tarif: "Sur devis",
      quoteKey: "gardiennage",
      image: `${API_URL}/api/uploads/service-gardiennage-bateau.jpg`,
      forBoth: true
    },
    {
      icon: <Home size={40} />,
      title: "Conciergerie nautique",
      description: "Profitez de votre bateau sans la logistique. Préparation de sortie, avitaillement, ménage, démarches administratives, coordination de prestataires : vous arrivez, vous naviguez.",
      features: [
        "Préparation complète avant arrivée",
        "Avitaillement courses & boissons",
        "Ménage avant/après navigation",
        "Démarches administratives (port, douanes, francisation)",
        "Coordination chantiers, électronique, voilerie"
      ],
      tarif: "Sur devis",
      quoteKey: "conciergerie",
      image: `${API_URL}/api/uploads/service-conciergerie-nautique.jpg`,
      forBoth: true
    }
  ];

  const benefits = [
    { icon: <ShieldCheck size={24} />, text: "Éviter les litiges" },
    { icon: <Clock size={24} />, text: "Gagner du temps" },
    { icon: <Target size={24} />, text: "Protéger votre capital" },
    { icon: <Users size={24} />, text: "Négocier en position de force" }
  ];

  return (
    <section id="services" className="relative py-24 md:py-32 bg-[#1a1c1b] overflow-hidden">
      {/* Sea background — same aesthetic as Hero, heavier overlay for readability */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <img
          src="https://images.pexels.com/photos/1295036/pexels-photo-1295036.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover"
        />
        {/* Dark gradient overlay to keep text/cards readable */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1c1b]/80 via-[#1a1c1b]/40 to-[#1a1c1b]/85"></div>
      </div>

      <div className="relative z-10 max-w-[87.5rem] mx-auto px-5 md:px-10">
        {/* Header with value proposition */}
        <div className="text-center mb-12">
          <h2 className="font-black text-5xl md:text-7xl leading-tight text-[#d9fb06] uppercase mb-6">
            Mes Services
          </h2>
          <p className="text-xl md:text-2xl text-[#dfddd6] max-w-4xl mx-auto mb-4">
            Avec <span className="text-[#d9fb06] font-bold">ValidatedBoat</span>, vous sécurisez votre transaction avant qu'il ne soit trop tard.
          </p>
          <p className="text-lg text-[#888680] max-w-3xl mx-auto">
            Acheteur ou vendeur, bénéficiez d'un accompagnement expert pour une transaction sereine.
          </p>
        </div>

        {/* Core services highlights */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 max-w-4xl mx-auto">
          <div className="bg-[#302f2c] border border-[#3f4816] rounded-lg p-4 text-center">
            <Search className="text-[#d9fb06] mx-auto mb-2" size={28} />
            <p className="text-[#dfddd6] text-sm font-medium">Audit documentaire complet</p>
          </div>
          <div className="bg-[#302f2c] border border-[#3f4816] rounded-lg p-4 text-center">
            <FileText className="text-[#d9fb06] mx-auto mb-2" size={28} />
            <p className="text-[#dfddd6] text-sm font-medium">Vérification historique</p>
          </div>
          <div className="bg-[#302f2c] border border-[#3f4816] rounded-lg p-4 text-center">
            <ClipboardCheck className="text-[#d9fb06] mx-auto mb-2" size={28} />
            <p className="text-[#dfddd6] text-sm font-medium">Analyse technique</p>
          </div>
          <div className="bg-[#302f2c] border border-[#3f4816] rounded-lg p-4 text-center">
            <Handshake className="text-[#d9fb06] mx-auto mb-2" size={28} />
            <p className="text-[#dfddd6] text-sm font-medium">Mise en relation qualifiée</p>
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-gradient-to-r from-[#302f2c] to-[#3f4816]/30 rounded-2xl p-8 mb-16">
          <p className="text-center text-[#dfddd6] mb-6 text-lg">
            Que vous soyez <span className="text-[#d9fb06] font-bold">propriétaire prêt à vendre</span> ou <span className="text-[#d9fb06] font-bold">acquéreur sur le point d'investir</span>, nous vous aidons à :
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {benefits.map((benefit) => (
              <div key={benefit.text} className="flex items-center gap-3 bg-[#1a1c1b]/50 rounded-lg p-4">
                <div className="text-[#d9fb06]">{benefit.icon}</div>
                <span className="text-[#dfddd6] font-medium">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Cards for Buyers and Sellers */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {/* For Sellers */}
          <div className="bg-[#302f2c] border-2 border-[#d9fb06] rounded-2xl p-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-[#d9fb06] text-[#1a1c1b] px-4 py-1 text-sm font-bold uppercase rounded-bl-lg">
              Vendeurs
            </div>
            <h3 className="text-2xl font-bold text-[#d9fb06] mb-4 mt-4">
              Vous avez un bateau à vendre ?
            </h3>
            <p className="text-[#dfddd6] mb-6">
              Recevez une <strong>pré-validation</strong> pour rassurer vos acheteurs et <strong>accélérer la vente</strong>. Un certificat qui fait la différence sur le marché.
            </p>
            <Link to="/vendre-mon-bateau">
              <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c4e205] font-bold rounded-full px-6 py-3 uppercase">
                Faire estimer mon bateau
              </Button>
            </Link>
          </div>

          {/* For Buyers */}
          <div className="bg-[#302f2c] border-2 border-[#3f4816] hover:border-[#d9fb06] transition-colors rounded-2xl p-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-[#3f4816] text-[#d9fb06] px-4 py-1 text-sm font-bold uppercase rounded-bl-lg">
              Acheteurs
            </div>
            <h3 className="text-2xl font-bold text-[#d9fb06] mb-4 mt-4">
              Vous cherchez à acheter ?
            </h3>
            <p className="text-[#dfddd6] mb-6">
              Demandez un <strong>contrôle avant signature</strong>. Ne prenez pas de risques sur un investissement aussi important. Protégez votre capital.
            </p>
            <Link to="/#contact">
              <Button className="bg-transparent border-2 border-[#d9fb06] text-[#d9fb06] hover:bg-[#d9fb06] hover:text-[#1a1c1b] font-bold rounded-full px-6 py-3 uppercase transition-colors">
                Demander un audit
              </Button>
            </Link>
          </div>
        </div>

        <h3 className="text-3xl font-bold text-[#d9fb06] text-center mb-8 uppercase">
          Tous nos services détaillés
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <Card 
              key={service.title}
              className="bg-[#302f2c] border border-[#3f4816]/50 hover:border-[#d9fb06]/50 transition-all duration-300 hover:transform hover:scale-[1.02] overflow-hidden group"
            >
              {/* Image */}
              <div className="h-48 overflow-hidden relative">
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                
                {/* Badge for target audience */}
                <div className="absolute top-3 left-3 flex gap-2">
                  {service.forBuyer && !service.forBoth && (
                    <span className="bg-[#3f4816] text-[#d9fb06] px-3 py-1 rounded-full text-xs font-bold uppercase">
                      Acheteur
                    </span>
                  )}
                  {service.forSeller && !service.forBoth && (
                    <span className="bg-[#d9fb06] text-[#1a1c1b] px-3 py-1 rounded-full text-xs font-bold uppercase">
                      Vendeur
                    </span>
                  )}
                  {service.forBoth && (
                    <span className="bg-gradient-to-r from-[#d9fb06] to-[#3f4816] text-[#1a1c1b] px-3 py-1 rounded-full text-xs font-bold uppercase">
                      Acheteur & Vendeur
                    </span>
                  )}
                </div>
                
                {/* Overlay with icon (only for services without target) */}
                {!service.hasTarget && (
                  <div className="absolute inset-0 bg-[#1a1c1b]/60 flex items-center justify-center">
                    <div className="text-[#d9fb06]">
                      {service.icon}
                    </div>
                  </div>
                )}
                
                {/* Crosshair/Target overlay for search service */}
                {service.hasTarget && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    {/* Crosshair lines */}
                    <div className="absolute w-full h-0.5 bg-[#d9fb06]"></div>
                    <div className="absolute h-full w-0.5 bg-[#d9fb06]"></div>
                    {/* Center circle */}
                    <div className="w-16 h-16 rounded-full border-2 border-[#d9fb06]"></div>
                    {/* Outer circle */}
                    <div className="w-24 h-24 rounded-full border border-[#d9fb06]/70"></div>
                    {/* Corner brackets for targeting effect */}
                    <div className="absolute top-6 left-6 w-8 h-8 border-t-2 border-l-2 border-[#d9fb06]"></div>
                    <div className="absolute top-6 right-6 w-8 h-8 border-t-2 border-r-2 border-[#d9fb06]"></div>
                    <div className="absolute bottom-6 left-6 w-8 h-8 border-b-2 border-l-2 border-[#d9fb06]"></div>
                    <div className="absolute bottom-6 right-6 w-8 h-8 border-b-2 border-r-2 border-[#d9fb06]"></div>
                  </div>
                )}
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-[#d9fb06] mb-3 uppercase">
                  {service.title}
                </h3>
                <p className="text-[#888680] mb-4 text-sm leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-[#d9fb06] rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-[#dfddd6] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 border-t border-[#3f4816]">
                  <p className="text-[#d9fb06] font-bold text-lg">{service.tarif}</p>
                  {service.quoteKey && (
                    <Button
                      type="button"
                      onClick={() =>
                        setQuoteService({ key: service.quoteKey, label: service.title })
                      }
                      className="mt-3 w-full bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c4e205] font-bold rounded-full py-2.5 uppercase flex items-center justify-center gap-2"
                      data-testid={`btn-quote-${service.quoteKey}`}
                    >
                      <Mail size={16} />
                      Demander un devis
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <QuoteRequestModal
        open={!!quoteService}
        onClose={() => setQuoteService(null)}
        service={quoteService}
      />
    </section>
  );
};

export default ServicesSection;